import {
  type UploadChangeParam,
  type UploadFile,
  type RcFile
} from 'antd/es/upload'
import { type UploadRequestOption as RcCustomRequestOptions } from 'rc-upload/lib/interface'
import { useCallback, useState } from 'react'

import { MAX_UPLOAD_FILE_SIZE } from '@/constants'
import { ERROR_MESSAGES } from '@/constants/common/message.constants'

import { uploadFile } from '../api'
import { type UploadFileResult } from '../models'

export type UseFileUploadProps = {
  /**
   * 選択ファイル
   */
  value?: UploadFileResult
  /** 選択されたファイルが有効かどうかを判定する関数
   * - 返り値が指定された場合、エラーメッセージとして表示されアップロードを中止する
   */
  isValidFile?: (file: RcFile) => string | null
  /**
   * アップロードされたファイルが変更された時に呼ばれるコールバック
   */
  onChangeUploadFile: (result: UploadFileResult | null) => void
}

type UseFileUpload = {
  /**
   * 選択中のファイル一覧
   */
  fileList: UploadFile[]
  /**
   * エラーメッセージ
   */
  errorMessage: string | null
  /**
   * アップロード処理
   */
  uploadRequest: (
    info: RcCustomRequestOptions<UploadFileResult>
  ) => Promise<void>
  /**
   * アップロード処理が開始される前の処理
   */
  beforeUpload: (file: RcFile, fileList: RcFile[]) => boolean
  /**
   * アップロード状態が変更された時のハンドラ
   */
  onUploadChange: (
    info: UploadChangeParam<UploadFile<UploadFileResult>>
  ) => void
  /**
   * ファイルの削除ボタンのハンドラ
   */
  onDelete: () => void
}

/**
 * ファイルアップロード処理のフック
 * - 選択されたファイルをアップロードし、アップロードされたファイルのキーを返す
 */
export const useFileUpload = ({
  value,
  isValidFile,
  onChangeUploadFile
}: UseFileUploadProps): UseFileUpload => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [fileList, setFileList] = useState<UploadFile[]>(
    value
      ? [
          {
            uid: value.key,
            name: value.name
          }
        ]
      : []
  )

  const beforeUpload = useCallback(
    (file: RcFile) => {
      if (isValidFile) {
        const errorMessage = isValidFile(file)
        if (errorMessage) {
          setErrorMessage(errorMessage)
          onChangeUploadFile(null)
          return false
        }
      }
      if (file.size >= MAX_UPLOAD_FILE_SIZE) {
        setErrorMessage(
          ERROR_MESSAGES.OVER_MAX_FILE_SIZE_MB(MAX_UPLOAD_FILE_SIZE / 1048576)
        )
        onChangeUploadFile(null)
        return false
      }
      return true
    },
    [isValidFile, onChangeUploadFile]
  )

  const handleChange = useCallback((files: UploadFile[]) => {
    setFileList(() => [...files])
  }, [])

  const onDelete = useCallback(() => {
    setErrorMessage(null)
    onChangeUploadFile(null)
  }, [onChangeUploadFile])

  const onUploadChange = useCallback(
    ({ fileList }: UploadChangeParam<UploadFile<UploadFileResult>>) => {
      handleChange(fileList)
    },
    [handleChange]
  )

  const uploadRequest = useCallback(
    async ({
      file,
      onSuccess,
      onError
    }: RcCustomRequestOptions<UploadFileResult>) => {
      const rcFile = file as RcFile
      try {
        const response = await uploadFile(rcFile)
        onChangeUploadFile(response)
        if (onSuccess) {
          onSuccess(response)
        }
        setErrorMessage(null)
      } catch (error) {
        setErrorMessage(ERROR_MESSAGES.FAILED_TO_GET_UPLOAD_DESTINATION_URL)
        if (onChangeUploadFile) {
          onChangeUploadFile(null)
        }
        if (onError) {
          onError(new Error())
        }
      }
    },
    [onChangeUploadFile]
  )

  return {
    fileList,
    errorMessage,
    uploadRequest,
    beforeUpload,
    onDelete,
    onUploadChange
  }
}
